import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  MenuItem,
  InputAdornment,
  alpha,
  Collapse,
  Tooltip,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsIcon from '@mui/icons-material/Settings';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ScheduledCall, OutboundCallConfig } from '../types';
import { callSchedulingApi, trainerApi, internalChannelsApi } from '../services/api';
import Papa from 'papaparse';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AgentSelect from './AgentSelect';
import PhoneNumberSelect, { PhoneChannel, fetchPhoneChannels } from './PhoneNumberSelect';
import TimeInput from './TimeInput';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { getAgentLabel } from '../utils/agentUtils';

// Default values for a new call
const defaultCall: Omit<ScheduledCall, 'id'> = {
  from_number: '',
  to_number: '',
  context_parameters: {},
  agent_id: null,
  internal_channel_id: 0,
  schedule_dt: null,
  outbound_call_config: {
    calling_hours: {
      timezone: 'America/Los_Angeles',
      weekly_hours: {
        monday: { start_time: '09:00:00', end_time: '17:00:00' },
        tuesday: { start_time: '09:00:00', end_time: '17:00:00' },
        wednesday: { start_time: '09:00:00', end_time: '17:00:00' },
        thursday: { start_time: '09:00:00', end_time: '17:00:00' },
        friday: { start_time: '09:00:00', end_time: '17:00:00' }
      },
      special_closures: {}
    }
  },
  immediate: false
};

// Days of the week for calling hours
const daysOfWeek = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

interface BatchScheduleFormProps {
  onSuccess: () => void;
  onConfigSelected?: (config: { fromNumber: string; channelId: number; agentId: number | null }) => void;
  initialConfig?: { fromNumber: string; channelId: number; agentId: number | null } | null;
}

interface InitialConfig {
  fromNumber: string;
  channelId: number;
  agentId: number | null;
}

const BatchScheduleForm: React.FC<BatchScheduleFormProps> = ({ onSuccess, onConfigSelected, initialConfig: propInitialConfig }): JSX.Element => {
  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newCall, setNewCall] = useState<ScheduledCall>({...defaultCall});
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Global configuration that will apply to all calls
  const [globalConfig, setGlobalConfig] = useState<{
    timezone: string;
    scheduledTime: Date | null;
    closure_date: Date | null;
    closure_start: string;
    closure_end: string;
    special_closures: Record<string, any>;
    [key: string]: any; // Allow dynamic keys for day/time settings
  }>({
    timezone: 'America/Los_Angeles',
    scheduledTime: null,
    closure_date: null,
    closure_start: '00:00:00',
    closure_end: '23:59:59',
    special_closures: {}
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const [agentMapping, setAgentMapping] = useState<{ [key: number]: string }>({});

  const [config, setConfig] = useState<InitialConfig>(() => {
    if (propInitialConfig) {
      return {
        fromNumber: propInitialConfig.fromNumber,
        channelId: propInitialConfig.channelId,
        agentId: propInitialConfig.agentId
      };
    }
    const savedConfig = localStorage.getItem('savedConfig');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        return {
          fromNumber: parsed.fromNumber || '',
          channelId: parsed.channelId || 0,
          agentId: parsed.agentId || null
        };
      } catch (e) {
        return { fromNumber: '', channelId: 0, agentId: null };
      }
    }
    return { fromNumber: '', channelId: 0, agentId: null };
  });

  // Show main form if we have a saved config or initialConfig
  const [showMainForm, setShowMainForm] = useState(() => {
    return !!(propInitialConfig || (config.fromNumber && config.channelId && config.agentId));
  });

  const [channels, setChannels] = useState<PhoneChannel[]>([]);

  const [useCustomHours, setUseCustomHours] = useState(false);
  const [useRetryConfig, setUseRetryConfig] = useState(false);

  const methods = useForm<ScheduledCall>({
    defaultValues: {
      from_number: config.fromNumber,
      to_number: '',
      context_parameters: {},
      agent_id: config.agentId,
      internal_channel_id: config.channelId,
      schedule_dt: new Date().toISOString(),
      outbound_call_config: {},
      immediate: false
    }
  });

  useEffect(() => {
    if (config.fromNumber && config.channelId && config.agentId) {
      localStorage.setItem('savedConfig', JSON.stringify(config));
    }
  }, [config]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const results = await trainerApi.getAgents();
        const mapping: { [key: number]: string } = {};
        const outboundAgents = results.filter((item: any) => item.synth_agent?.outbound_call_enabled);
        
        outboundAgents.forEach((item: any) => {
          const id = item.synth_agent?.synth_agent_id || 0;
          const name = item.synth_display_name || item.synth_agent?.synth_name || 'Agent';
          const note = item.note || item.synth_agent?.note || '';
          mapping[id] = name + (note ? ` (${note})` : '');
        });
        setAgentMapping(mapping);

        // If no agent is selected and we have outbound agents, select the first one
        if (!config.agentId && outboundAgents.length > 0) {
          const firstAgent = outboundAgents[0];
          const agentId = firstAgent.synth_agent?.synth_agent_id || 0;
          setConfig(prev => ({ ...prev, agentId }));
        }
      } catch (error) {
        console.error('Error fetching agents', error);
      }
    };
    fetchAgents();
  }, []);

  useEffect(() => {
    // Load channels from cache or fetch them
    fetchPhoneChannels()
      .then(channels => {
        setChannels(channels);
        // If no phone number is selected and we have channels, select the first one
        if (!config.fromNumber && channels.length > 0) {
          const firstChannel = channels[0];
          setConfig(prev => ({
            ...prev,
            fromNumber: firstChannel.channel_value,
            channelId: parseInt(firstChannel.channel_id)
          }));
        }
      })
      .catch(error => console.error('Error loading phone channels:', error));
  }, []);

  const handleAddCall = () => {
    // Validate required fields
    if (!newCall.to_number) {
      setError('Please fill in the To Number field');
      return;
    }

    // Validate scheduled time
    if (!globalConfig.scheduledTime) {
      setError('Please set a batch schedule time in the Global Call Settings');
      return;
    }

    // Create a processed call with current global settings
    const processedCall = {
      ...newCall,
      from_number: config.fromNumber,
      agent_id: config.agentId,
      internal_channel_id: config.channelId,
      context_parameters: typeof newCall.context_parameters === 'string' 
        ? JSON.parse(newCall.context_parameters) 
        : newCall.context_parameters,
      outbound_call_config: {
        ...(typeof newCall.outbound_call_config === 'string' 
          ? JSON.parse(newCall.outbound_call_config) as OutboundCallConfig
          : newCall.outbound_call_config as OutboundCallConfig),
        agent_id: config.agentId,
        calling_hours: {
          timezone: globalConfig.timezone,
          weekly_hours: generateWeeklyHours(),
          special_closures: {}
        }
      },
      immediate: false,
      schedule_dt: globalConfig.scheduledTime.toISOString()
    };

    // Add the new call to the list
    setCalls([...calls, processedCall]);
    
    // Reset the form
    setNewCall({
      ...defaultCall,
      from_number: config.fromNumber,
      agent_id: config.agentId,
      internal_channel_id: config.channelId
    });
  };

  const handleRemoveCall = (index: number) => {
    const updatedCalls = [...calls];
    updatedCalls.splice(index, 1);
    setCalls(updatedCalls);
  };

  const handleInputChange = (field: keyof ScheduledCall, value: any) => {
    setNewCall(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContextParamsChange = (value: string) => {
    try {
      // Parse the JSON string into an object
      const parsedValue = value ? JSON.parse(value) : {};
      setNewCall(prev => ({
        ...prev,
        context_parameters: parsedValue
      }));
    } catch (e) {
      // If it's not valid JSON, just show the error but don't change the value
      setError('Invalid JSON format for context parameters');
    }
  };

  const handleGlobalConfigChange = (field: string, value: any) => {
    setGlobalConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Convert time object to string format HH:MM:SS
  const formatTimeToString = (time: Date | null): string => {
    if (!time) return '00:00:00';
    return format(time, 'HH:mm:ss');
  };

  // Convert string time to Date object
  const parseTimeString = (timeStr: string): Date => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, seconds);
    return date;
  };

  // Generate weekly hours based on enabled days
  const generateWeeklyHours = () => {
    const weeklyHours: any = {};
    daysOfWeek.forEach(day => {
      if (enabledDays[day.value]) {
        weeklyHours[day.value] = { 
          start_time: globalConfig[`${day.value}_start`] || '09:00:00', 
          end_time: globalConfig[`${day.value}_end`] || '17:00:00' 
        };
      }
    });
    return weeklyHours;
  };

  // Handle day toggle
  const handleDayToggle = (day: string, checked: boolean) => {
    setEnabledDays(prev => ({ ...prev, [day]: checked }));
  };

  // Handle time change for a specific day
  const handleTimeChange = (field: string, value: Date | null) => {
    if (value) {
      const timeString = format(value, 'HH:mm:ss');
      setGlobalConfig((prev) => ({
        ...prev,
        [field]: timeString,
      }));
    }
  };

  // Handle CSV upload
  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setCsvFile(files[0]);
      parseCsvFile(files[0]);
      // Reset the file input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Parse CSV file
  const parseCsvFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        processCsvResults(results.data);
      },
      error: (error) => {
        setError(`CSV parsing error: ${error.message}`);
      }
    });
  };

  // Validate initial configuration
  const isInitialConfigValid = () => {
    return config.fromNumber && config.channelId && config.agentId;
  };

  // Handle initial config changes
  const handleInitialConfigChange = (field: keyof InitialConfig, value: string | number | null) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Initial configuration form
  const renderInitialConfig = () => {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: alpha('#000', 0.08) }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
            Basic Configuration
          </Typography>
          <Stack spacing={3}>
            <PhoneNumberSelect
              value={config.fromNumber}
              onChange={(value, channelId) => {
                handleInitialConfigChange('fromNumber', value);
                handleInitialConfigChange('channelId', parseInt(channelId));
              }}
              label="From Number"
              channels={channels}
            />
            <AgentSelect
              value={config.agentId}
              onChange={(value) => handleInitialConfigChange('agentId', value)}
              label="Agent"
            />
            <Button
              variant="contained"
              disabled={!isInitialConfigValid()}
              onClick={() => {
                methods.setValue('from_number', config.fromNumber);
                methods.setValue('internal_channel_id', config.channelId);
                methods.setValue('agent_id', config.agentId);
                setShowMainForm(true);
              }}
              sx={{
                py: 2,
                backgroundColor: '#1e293b',
                '&:hover': {
                  backgroundColor: alpha('#1e293b', 0.9),
                },
              }}
            >
              Continue to Schedule
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  };

  // Process CSV results into calls
  const processCsvResults = (data: any[]) => {
    try {
      const parsedCalls = data.map((row) => {
        // Basic required fields
        if (!row.phone) {
          throw new Error(`Missing required phone number in CSV row: ${JSON.stringify(row)}`);
        }

        // Build context parameters from all non-phone columns
        const contextParams: Record<string, string> = {};
        Object.entries(row).forEach(([key, value]) => {
          // Skip phone column and empty values
          if (key !== 'phone' && value) {
            contextParams[key] = value.toString();
          }
        });

        // Create call with default config and global settings
        const call: ScheduledCall = {
          from_number: config.fromNumber,
          to_number: row.phone,
          agent_id: config.agentId,
          internal_channel_id: config.channelId,
          context_parameters: contextParams,
          schedule_dt: globalConfig.scheduledTime ? globalConfig.scheduledTime.toISOString() : null,
          immediate: false,
          outbound_call_config: {
            agent_id: config.agentId,
            calling_hours: {
              timezone: globalConfig.timezone,
              weekly_hours: generateWeeklyHours(),
              special_closures: {}
            }
          }
        };

        return call;
      });

      // Append new calls to existing ones instead of replacing
      setCalls(prevCalls => [...prevCalls, ...parsedCalls]);
      setSuccess(`Successfully loaded ${parsedCalls.length} calls from CSV. Total calls: ${calls.length + parsedCalls.length}`);
    } catch (err: any) {
      setError(err.message || 'Failed to process CSV data');
    }
  };

  // Generate sample CSV
  const generateSampleCsv = () => {
    const sampleData = [
      {
        phone: '+15559876543',
        name: 'John Doe'
      },
      {
        phone: '+15553334444',
        name: 'Jane Smith'
      }
    ];

    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'sample_batch_calls.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    if (calls.length === 0) {
      setError('Please add at least one call to schedule');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Prepare calls data
      const callsData = calls.map(call => {
        const processedCall = { ...call };
        
        // Ensure outbound_call_config is an object
        if (typeof processedCall.outbound_call_config === 'string') {
          try {
            processedCall.outbound_call_config = JSON.parse(processedCall.outbound_call_config) as OutboundCallConfig;
          } catch (e) {
            console.error('Error parsing outbound_call_config:', e);
            processedCall.outbound_call_config = {} as OutboundCallConfig;
          }
        }

        // Only include calling hours if custom hours are enabled
        if (!useCustomHours) {
          if (processedCall.outbound_call_config) {
            delete processedCall.outbound_call_config.calling_hours;
          }
        }

        // Only include retry config if enabled
        if (!useRetryConfig) {
          if (processedCall.outbound_call_config) {
            delete processedCall.outbound_call_config.retry_config;
          }
        }
        
        // Ensure agent_id is in outbound_call_config
        const config = processedCall.outbound_call_config as OutboundCallConfig;
        if (!config.agent_id) {
          config.agent_id = processedCall.agent_id;
        }
        
        // Ensure context_parameters is an object
        if (typeof processedCall.context_parameters === 'string') {
          try {
            processedCall.context_parameters = JSON.parse(processedCall.context_parameters as string);
          } catch (e) {
            console.error('Error parsing context_parameters:', e);
            processedCall.context_parameters = {};
          }
        } else if (!processedCall.context_parameters) {
          processedCall.context_parameters = {};
        }

        // Ensure schedule_dt is set from globalConfig
        if (globalConfig.scheduledTime) {
          processedCall.schedule_dt = globalConfig.scheduledTime.toISOString();
        }
          
        return processedCall;
      });

      const response = await callSchedulingApi.scheduleBatchCalls({ calls: callsData });
      setSuccess(`Successfully scheduled ${calls.length} calls`);
      setCalls([]);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to schedule calls');
    } finally {
      setLoading(false);
    }
  };

  // Trigger file input click
  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddClosure = () => {
    if (globalConfig.closure_date) {
      const dateString = format(globalConfig.closure_date, 'yyyy-MM-dd');
      setGlobalConfig((prev) => ({
        ...prev,
        special_closures: {
          ...prev.special_closures,
          [dateString]: {
            start_time: globalConfig.closure_start,
            end_time: globalConfig.closure_end,
          },
        },
        closure_date: null,
      }));
    }
  };

  const handleRemoveClosure = (date: string) => {
    setGlobalConfig((prev) => {
      const newClosures = { ...prev.special_closures };
      delete newClosures[date];
      return {
        ...prev,
        special_closures: newClosures,
      };
    });
  };

  const handleContinue = () => {
    if (onConfigSelected) {
      onConfigSelected({
        fromNumber: config.fromNumber,
        channelId: config.channelId,
        agentId: config.agentId
      });
    } else {
      setShowMainForm(true);
    }
  };

  return (
    <>
      {!showMainForm ? (
        renderInitialConfig()
      ) : (
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Stack spacing={4}>
            {/* Show selected configuration */}
            <Box sx={{ 
              p: 2, 
              borderRadius: 1,
              bgcolor: alpha('#1e293b', 0.03),
              border: '1px solid',
              borderColor: alpha('#1e293b', 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Configuration
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Typography variant="body2">
                    <Box component="span" sx={{ color: 'text.secondary' }}>From:</Box> {config.fromNumber}
                  </Typography>
                  <Typography variant="body2">
                    <Box component="span" sx={{ color: 'text.secondary' }}>Agent:</Box> {agentMapping[config.agentId || 0] || 'None'}
                  </Typography>
                </Stack>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowMainForm(false)}
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha('#1e293b', 0.04),
                    borderColor: 'primary.main',
                  },
                }}
              >
                Change Configuration
              </Button>
            </Box>

            {/* Upload/Download Section */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha('#000', 0.02),
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={triggerFileUpload}
                sx={{
                  flex: 1,
                  py: 2,
                  borderStyle: 'dashed',
                }}
              >
                Upload CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={generateSampleCsv}
                sx={{
                  flex: 1,
                  py: 2,
                  borderStyle: 'dashed',
                }}
              >
                Download Sample CSV
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCsvUpload}
              />
            </Stack>

            {/* Global Settings */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                <SettingsIcon sx={{ mr: 1 }} />
                Global Call Settings
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Grid container spacing={3}>
                  {/* Schedule Time */}
                  <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={8}>
                        <DatePicker
                          label="Batch Schedule Date"
                          value={globalConfig.scheduledTime}
                          onChange={(newDate) => {
                            if (newDate && !isNaN(newDate.getTime())) {
                              const currentDate = globalConfig.scheduledTime || new Date();
                              const updatedDate = new Date(newDate);
                              updatedDate.setHours(currentDate.getHours());
                              updatedDate.setMinutes(currentDate.getMinutes());
                              handleGlobalConfigChange('scheduledTime', updatedDate);
                            } else {
                              handleGlobalConfigChange('scheduledTime', null);
                            }
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!(globalConfig.scheduledTime && isNaN(globalConfig.scheduledTime.getTime())),
                              helperText: globalConfig.scheduledTime && isNaN(globalConfig.scheduledTime.getTime()) ? 'Invalid date' : '',
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TimeInput
                          value={globalConfig.scheduledTime}
                          onChange={(newDate) => {
                            if (newDate && !isNaN(newDate.getTime())) {
                              handleGlobalConfigChange('scheduledTime', newDate);
                            } else {
                              handleGlobalConfigChange('scheduledTime', null);
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Custom Hours Toggle */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useCustomHours}
                          onChange={(e) => setUseCustomHours(e.target.checked)}
                        />
                      }
                      label="Use Custom Calling Hours"
                    />
                  </Grid>

                  {/* Calling Hours Section - Only show if enabled */}
                  <Grid item xs={12}>
                    <Collapse in={useCustomHours}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mt: 2 }}>
                        Calling Hours
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="outbound_call_config.calling_hours.timezone"
                            control={methods.control}
                            defaultValue="America/Los_Angeles"
                            render={({ field }) => (
                              <TextField
                                select
                                fullWidth
                                label="Timezone"
                                {...field}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: alpha('#1e293b', 0.02),
                                    '&:hover': {
                                      backgroundColor: alpha('#1e293b', 0.03),
                                    },
                                    '&.Mui-focused': {
                                      backgroundColor: '#fff',
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                                <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                                <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                                <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                                <MenuItem value="America/Anchorage">Alaska Time (AKT)</MenuItem>
                                <MenuItem value="Pacific/Honolulu">Hawaii Time (HT)</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>

                        {/* Custom Hours */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mt: 2 }}>
                            Custom Hours
                          </Typography>
                          <Grid container spacing={2}>
                            {daysOfWeek.map((day) => (
                              <Grid item xs={12} sm={6} key={day.value}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={enabledDays[day.value]}
                                      onChange={(e) => handleDayToggle(day.value, e.target.checked)}
                                    />
                                  }
                                  label={day.label}
                                />
                                
                                {enabledDays[day.value] && (
                                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <TimePicker
                                      label="Start Time"
                                      value={parseTimeString(globalConfig[`${day.value}_start`] || '09:00:00')}
                                      onChange={(newValue) => handleTimeChange(`${day.value}_start`, newValue)}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          size: "small",
                                          sx: {
                                            backgroundColor: alpha('#000', 0.02),
                                            borderRadius: 2
                                          }
                                        }
                                      }}
                                    />
                                    <TimePicker
                                      label="End Time"
                                      value={parseTimeString(globalConfig[`${day.value}_end`] || '17:00:00')}
                                      onChange={(newValue) => handleTimeChange(`${day.value}_end`, newValue)}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          size: "small",
                                          sx: {
                                            backgroundColor: alpha('#000', 0.02),
                                            borderRadius: 2
                                          }
                                        }
                                      }}
                                    />
                                  </Stack>
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>

                        {/* Special Closures */}
                        <Box sx={{ mt: 4 }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                            Special Closures
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <DatePicker
                                label="Closure Date"
                                value={globalConfig.closure_date || null}
                                onChange={(date) => handleGlobalConfigChange('closure_date', date)}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    sx: {
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha('#000', 0.02)
                                      }
                                    }
                                  }
                                }}
                              />
                            </Grid>
                            {globalConfig.closure_date && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <TimePicker
                                    label="Start Time"
                                    value={parseTimeString(globalConfig.closure_start || '00:00:00')}
                                    onChange={(time) => handleTimeChange('closure_start', time)}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                          backgroundColor: alpha('#000', 0.02),
                                          borderRadius: 2
                                        }
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TimePicker
                                    label="End Time"
                                    value={parseTimeString(globalConfig.closure_end || '23:59:59')}
                                    onChange={(time) => handleTimeChange('closure_end', time)}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        size: "small",
                                        sx: {
                                          backgroundColor: alpha('#000', 0.02),
                                          borderRadius: 2
                                        }
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <Button
                                    variant="outlined"
                                    onClick={handleAddClosure}
                                    sx={{
                                      borderStyle: 'dashed',
                                      color: 'text.secondary'
                                    }}
                                  >
                                    Add Closure
                                  </Button>
                                </Grid>
                              </>
                            )}
                          </Grid>

                          {/* Display existing closures */}
                          {Object.entries(globalConfig.special_closures || {}).length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Existing Closures
                              </Typography>
                              <Stack spacing={1}>
                                {Object.entries(globalConfig.special_closures || {}).map(([date, times]: [string, any]) => (
                                  <Paper
                                    key={date}
                                    sx={{
                                      p: 2,
                                      backgroundColor: alpha('#000', 0.02),
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {format(new Date(date), 'MMM d, yyyy')} ({times.start_time} - {times.end_time})
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveClosure(date)}
                                      sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                          color: 'error.main'
                                        }
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Collapse>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Add New Call Form */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha('#000', 0.08),
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                Add New Call
              </Typography>

              <Stack spacing={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="To Number"
                      value={newCall.to_number}
                      onChange={(e) => handleInputChange('to_number', e.target.value)}
                      fullWidth
                      placeholder="+15551234567"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: alpha('#000', 0.02),
                          '&:hover': {
                            backgroundColor: alpha('#000', 0.03),
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#fff',
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Context Parameters */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mt: 2 }}>
                      Context Parameters
                    </Typography>
                    <Stack spacing={2}>
                      {Object.entries(newCall.context_parameters || {}).map(([key, value], index) => (
                        <Paper
                          key={index}
                          sx={{
                            p: 2,
                            backgroundColor: alpha('#000', 0.02),
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                          }}
                        >
                          <TextField
                            size="small"
                            label="Key"
                            value={key}
                            onChange={(e) => {
                              const params = { ...(newCall.context_parameters as Record<string, string>) };
                              const oldValue = params[key];
                              delete params[key];
                              params[e.target.value] = oldValue;
                              handleInputChange('context_parameters', params);
                            }}
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            size="small"
                            label="Value"
                            value={value}
                            onChange={(e) => {
                              const params = { ...(newCall.context_parameters as Record<string, string>) };
                              params[key] = e.target.value;
                              handleInputChange('context_parameters', params);
                            }}
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const params = { ...(newCall.context_parameters as Record<string, string>) };
                              delete params[key];
                              handleInputChange('context_parameters', params);
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Paper>
                      ))}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const params = { ...(newCall.context_parameters as Record<string, string>) };
                          let newKey = 'new_key';
                          let counter = 1;
                          while (params[newKey]) {
                            newKey = `new_key_${counter}`;
                            counter++;
                          }
                          params[newKey] = '';
                          handleInputChange('context_parameters', params);
                        }}
                        sx={{
                          borderStyle: 'dashed',
                          color: 'text.secondary',
                        }}
                      >
                        Add Context Parameter
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                <Button
                  onClick={handleAddCall}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{
                    mt: 2,
                    alignSelf: 'flex-end',
                    borderStyle: 'dashed',
                  }}
                >
                  Add to Batch
                </Button>
              </Stack>
            </Paper>

            {/* Calls Table */}
            {calls.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#000', 0.08),
                  overflow: 'hidden',
                }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Agent</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {calls.map((call, index) => (
                        <TableRow key={index}>
                          <TableCell>{call.from_number}</TableCell>
                          <TableCell>{call.to_number}</TableCell>
                          <TableCell>{agentMapping[call.agent_id || 0] || 'None'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove">
                              <IconButton
                                onClick={() => handleRemoveCall(index)}
                                size="small"
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'error.main',
                                    backgroundColor: alpha('#000', 0.04),
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Submit Button */}
            {calls.length > 0 && (
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                onClick={handleSubmit}
                sx={{
                  py: 2,
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Schedule Calls'}
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BatchScheduleForm; 